
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Copy, Check, BadgeIndianRupee } from 'lucide-react';
import { toast } from 'sonner';

interface ReferralSectionProps {
  userEmail: string | undefined;
}

const ReferralSection = ({ userEmail }: ReferralSectionProps) => {
  const [copied, setCopied] = useState(false);
  
  // Generate a stable referral code based on email
  const generateReferralCode = (email: string | undefined) => {
    if (!email) return 'WEALTH123';
    const username = email.split('@')[0];
    // Use a consistent method to generate a code
    return (username.substring(0, 6).toUpperCase() + '123').replace(/[^A-Z0-9]/g, '');
  };
  
  const referralCode = generateReferralCode(userEmail);
  const referralLink = `${window.location.origin}/auth?ref=${referralCode}`;

  // Mock data for active referrals - would come from backend
  const activeReferrals = [
    { id: 1, name: 'Amit Kumar', status: 'Signed Up', date: '2024-04-10' },
    { id: 2, name: 'Priya Singh', status: 'Completed Profile', date: '2024-04-05' }
  ];

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied to clipboard!');
    
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  return (
    <Card className="bg-black border-[#9E7C47]/30">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[#9E7C47]" />
          <CardTitle>Refer & Earn Program</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-white/70 mb-4">
          Invite friends to earn ₹1,000 in investment credits for each successful referral!
          Your friends will also receive ₹1,000 in credits when they sign up.
          <div className="mt-2 text-xs bg-[#9E7C47]/10 p-2 rounded">
            <BadgeIndianRupee className="h-3 w-3 inline mb-0.5 mr-1 text-[#9E7C47]" />
            <span className="font-medium">Referral Limit:</span> Up to ₹10,000 in lifetime referral credits.
          </div>
        </div>
        
        <div className="mb-6">
          <label htmlFor="referralLink" className="text-sm font-medium text-white/70 mb-1.5 block">
            Your Referral Link
          </label>
          <div className="flex gap-2">
            <Input 
              id="referralLink"
              value={referralLink} 
              readOnly 
              className="bg-[#9E7C47]/5 border-[#9E7C47]/30 text-white"
            />
            <Button onClick={handleCopyLink} variant="outline" className="border-[#9E7C47] text-[#9E7C47]">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        
        {activeReferrals.length > 0 && (
          <div>
            <h3 className="font-medium mb-2">Active Referrals</h3>
            <div className="rounded-md border border-white/10 overflow-hidden">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-[#9E7C47]/10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {activeReferrals.map((referral) => (
                    <tr key={referral.id}>
                      <td className="px-4 py-2 text-sm">{referral.name}</td>
                      <td className="px-4 py-2 text-sm text-[#9E7C47]">{referral.status}</td>
                      <td className="px-4 py-2 text-sm text-white/70">{referral.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralSection;
